// src/payments/paymentsApi.ts
import { apiClient } from "@maat/api-base";
var paymentsApi = {
  /**
   * Which processor should this payer be sent to? Called BEFORE checkout
   * renders and again after a switch: `ROUTED` renders that provider with a
   * quiet override, anything else renders the picker. Intention creation takes
   * the answer as an INSTRUCTION and never re-runs the model.
   *
   * Everything the model reads is server-resolved except `device_vote`, and
   * there is NO subject parameter — it answers for the authenticated caller.
   */
  getRoutingDecision: async (data = {}) => {
    return apiClient.post("/payment/api/payments/routing/decision", data);
  },
  createIntention: async (data) => {
    return apiClient.post("/payment/api/payments/intention", data);
  },
  /**
   * The checkout context behind "try the other checkout", for a payer whose card
   * was refused. UNAUTHENTICATED by necessity — the decline redirect carries no
   * session — so `holdId` is a capability token: the endpoint answers the
   * MINIMUM needed to re-mint, in ONE shape for unknown, expired and
   * already-paid holds alike.
   */
  getDeclineRecovery: async (holdId) => {
    return apiClient.get(`/payment/api/payments/holds/${holdId}/recovery`);
  },
  capture: async (data) => {
    return apiClient.post("/payment/api/payments/capture", data);
  },
  // Saved cards (guest).
  createCardSetupIntention: async () => {
    return apiClient.post("/payment/api/payments/cards/setup-intention", {});
  },
  listSavedCards: async () => {
    return apiClient.get("/payment/api/payments/cards");
  },
  deleteSavedCard: async (id) => {
    return apiClient.delete(`/payment/api/payments/cards/${id}`);
  },
  // Billing profile — the guest's billing address.
  getBillingProfile: async () => {
    return apiClient.get("/payment/api/billing-profile");
  },
  updateBillingProfile: async (data) => {
    return apiClient.put("/payment/api/billing-profile", data);
  },
  // Workspace billing profile — business invoice details.
  getWorkspaceBillingProfile: async () => {
    return apiClient.get("/payment/api/workspace/billing-profile");
  },
  updateWorkspaceBillingProfile: async (data) => {
    return apiClient.put("/payment/api/workspace/billing-profile", data);
  },
  // Withdrawal methods.
  createWithdrawalMethod: async (data) => {
    return apiClient.post("/payment/api/withdrawal-methods", data);
  },
  /** `currency` narrows the picker to destinations that can receive it. */
  listWithdrawalMethods: async (workspaceId, currency) => {
    return apiClient.get(`/payment/api/withdrawal-methods/${workspaceId}`, {
      params: currency ? { currency } : void 0
    });
  },
  getWithdrawalMethod: async (workspaceId, methodId) => {
    return apiClient.get(`/payment/api/withdrawal-methods/${workspaceId}/${methodId}`);
  },
  updateWithdrawalMethod: async (methodId, data) => {
    return apiClient.put(`/payment/api/withdrawal-methods/${methodId}`, data);
  },
  setDefaultWithdrawalMethod: async (methodId) => {
    return apiClient.patch(`/payment/api/withdrawal-methods/${methodId}/default`, {});
  },
  deactivateWithdrawalMethod: async (methodId) => {
    return apiClient.delete(`/payment/api/withdrawal-methods/${methodId}`);
  },
  // Payouts.
  createPayout: async (data) => {
    return apiClient.post("/payment/api/payouts", data);
  },
  listPayouts: async (workspaceId, params) => {
    return apiClient.get(`/payment/api/payouts/${workspaceId}`, { params });
  },
  getPayout: async (workspaceId, payoutId) => {
    return apiClient.get(`/payment/api/payouts/${workspaceId}/${payoutId}`);
  },
  // Admin: workspace Finance tab — admin-token gated, any workspace.
  adminGetWorkspaceBalance: async (workspaceId) => {
    return apiClient.get(`/payment/api/payouts/admin/${workspaceId}/balance`);
  },
  adminGetWorkspacePayoutTimeSeries: async (workspaceId, months) => {
    return apiClient.get(`/payment/api/payouts/admin/${workspaceId}/timeseries`, {
      params: { months }
    });
  },
  adminListWorkspacePayouts: async (workspaceId, params) => {
    return apiClient.get(`/payment/api/payouts/admin/${workspaceId}/recent`, { params });
  },
  // Payout requests — host-initiated, token-scoped to the active workspace.
  getWorkspaceBalance: async () => {
    return apiClient.get("/payment/api/payout-requests/balance");
  },
  getListingEarnings: async (listingId) => {
    return apiClient.get("/payment/api/payout-requests/earnings", { params: { listingId } });
  },
  getUpcomingPayouts: async () => {
    return apiClient.get("/payment/api/payout-requests/upcoming");
  },
  /**
   * Bookings selectable for a payout. `currency` narrows to one partition — a
   * request reserves exactly one currency, so a mixed selection cannot be
   * reserved. Filtering is server-side; never split the list on the client.
   */
  getEligibleBookings: async (currency) => {
    return apiClient.get("/payment/api/payout-requests/eligible-bookings", {
      params: currency ? { currency } : void 0
    });
  },
  listPayoutRequests: async (params) => {
    return apiClient.get("/payment/api/payout-requests", { params });
  },
  getPayoutRequest: async (requestId) => {
    return apiClient.get(`/payment/api/payout-requests/${requestId}`);
  },
  getPayoutStatement: async (requestId) => {
    return apiClient.get(`/payment/api/payout-requests/${requestId}/statement`);
  },
  createPayoutRequest: async (data) => {
    return apiClient.post("/payment/api/payout-requests", data);
  },
  cancelPayoutRequest: async (requestId) => {
    return apiClient.post(`/payment/api/payout-requests/${requestId}/cancel`, {});
  },
  // Admin: payout review — admin-token gated, any workspace.
  adminListPayoutRequests: async (params) => {
    return apiClient.get("/payment/api/payout-requests/admin", {
      params,
      paramsSerializer: { indexes: null }
    });
  },
  adminGetPayoutRequest: async (requestId) => {
    return apiClient.get(`/payment/api/payout-requests/admin/${requestId}`);
  },
  // Admin: platform finance summary for the dashboard — admin-token gated.
  adminGetFinanceSummary: async () => {
    return apiClient.get("/payment/api/admin/finance/summary");
  },
  // Admin: Finance dashboard reporting — admin-token gated.
  adminListTransactions: async (params) => {
    return apiClient.get("/payment/api/admin/finance/transactions", { params });
  },
  adminGetTransaction: async (id) => {
    return apiClient.get(`/payment/api/admin/finance/transactions/${id}`);
  },
  adminListRefunds: async (params) => {
    return apiClient.get("/payment/api/admin/finance/refunds", { params });
  },
  adminGetRevenueBreakdown: async (params) => {
    return apiClient.get("/payment/api/admin/finance/revenue/breakdown", { params });
  },
  adminGetRevenueTimeSeries: async (months) => {
    return apiClient.get("/payment/api/admin/finance/revenue/timeseries", {
      params: { months }
    });
  },
  adminGetUpcomingReleases: async () => {
    return apiClient.get("/payment/api/admin/finance/upcoming-releases");
  },
  adminListOutstandingDebts: async (params) => {
    return apiClient.get("/payment/api/admin/debts", { params });
  },
  /**
   * The confidently-wrong metric — declines that followed a confident route,
   * beside the ambiguous ones the picker handled. Aggregates by outcome and
   * certainty band; there is deliberately NO row-level read.
   */
  adminGetRoutingTelemetry: async (days) => {
    return apiClient.get("/payment/api/admin/routing/telemetry", {
      params: days ? { days } : void 0
    });
  },
  adminGetBookingFinancials: async (bookingId) => {
    return apiClient.get(`/payment/api/admin/finance/bookings/${bookingId}`);
  },
  // The webhook inbox (admin_token session): ENVELOPES ONLY. There is no
  // `getPayload` and no endpoint behind one — a parked callback still holds the
  // verbatim PSP body: the payer's billing identity, masked PAN and, on
  // Stripe, the payment-method fingerprint.
  adminListWebhookInbox: async (params = {}) => {
    return apiClient.get("/payment/api/admin/webhooks/inbox", { params });
  },
  adminGetWebhookInboxStats: async () => {
    return apiClient.get("/payment/api/admin/webhooks/inbox/stats");
  },
  /**
   * Re-open a PARKED callback and re-run the drain — the same two calls the
   * quarantine-retry sweep makes, so the console and the cron cannot mean
   * different things. Refused (409) for a PROCESSED row (money settled, payload
   * now a redaction) and for a PENDING one the drain may be holding.
   */
  adminRetryWebhookInboxRow: async (id) => {
    return apiClient.post(`/payment/api/admin/webhooks/inbox/${id}/retry`);
  },
  fulfillPayoutRequest: async (requestId, data) => {
    return apiClient.post(`/payment/api/payout-requests/${requestId}/fulfill`, data ?? {});
  },
  rejectPayoutRequest: async (requestId, data) => {
    return apiClient.post(`/payment/api/payout-requests/${requestId}/reject`, data ?? {});
  },
  // Payout receipts (host rail). The admin presigns, lists every status, reveals
  // (audited) and deletes; the requester lists ready receipts of a PAID request
  // and reveals one at a time — each reveal is a fresh 15-minute URL.
  presignPayoutAttachment: async (requestId, data) => {
    return apiClient.post(
      `/payment/api/payout-requests/${requestId}/attachments/presign`,
      data
    );
  },
  adminListPayoutAttachments: async (requestId) => {
    return apiClient.get(`/payment/api/payout-requests/admin/${requestId}/attachments`);
  },
  adminRevealPayoutAttachment: async (requestId, attachmentId) => {
    return apiClient.post(
      `/payment/api/payout-requests/admin/${requestId}/attachments/${attachmentId}/view`
    );
  },
  deletePayoutAttachment: async (requestId, attachmentId) => {
    return apiClient.delete(
      `/payment/api/payout-requests/${requestId}/attachments/${attachmentId}`
    );
  },
  listPayoutAttachments: async (requestId) => {
    return apiClient.get(`/payment/api/payout-requests/${requestId}/attachments`);
  },
  revealPayoutAttachment: async (requestId, attachmentId) => {
    return apiClient.post(
      `/payment/api/payout-requests/${requestId}/attachments/${attachmentId}/view`
    );
  },
  adminGetWithdrawalMethod: async (methodId) => {
    return apiClient.get(`/payment/api/withdrawal-methods/admin/${methodId}`);
  },
  // Host debt (cancellation-penalty) self-pay.
  getDebtSummary: async () => {
    return apiClient.get("/payment/api/debts");
  },
  /** Settle debt in ONE currency partition from the same currency's balance. */
  payDebtFromBalance: async (currency) => {
    return apiClient.post(
      "/payment/api/debts/pay-from-balance",
      {},
      { params: currency ? { currency } : void 0 }
    );
  },
  createDebtPaymentIntention: async () => {
    return apiClient.post("/payment/api/debts/pay-by-card-intention", {});
  },
  // Penalty tracking (read model): summary, paginated records, per-booking.
  getPenaltyHistory: async () => {
    return apiClient.get("/payment/api/debts/history");
  },
  listPenalties: async (params) => {
    return apiClient.get("/payment/api/debts/penalties", { params });
  },
  getCollectedFromBooking: async (bookingId) => {
    return apiClient.get(`/payment/api/debts/collected-from/${bookingId}`);
  },
  // Settlement activity for a single penalty (per-penalty page timeline).
  getPenaltyCollections: async (penaltyId) => {
    return apiClient.get(`/payment/api/debts/penalty/${penaltyId}/collections`);
  },
  // Per-penalty (single record) settle.
  getPenalty: async (penaltyId) => {
    return apiClient.get(`/payment/api/debts/penalty/${penaltyId}`);
  },
  payPenaltyFromBalance: async (penaltyId) => {
    return apiClient.post(`/payment/api/debts/penalty/${penaltyId}/pay-from-balance`, {});
  },
  createPenaltyPaymentIntention: async (penaltyId) => {
    return apiClient.post(`/payment/api/debts/penalty/${penaltyId}/pay-by-card-intention`, {});
  }
};

export {
  paymentsApi
};
